import React, { useState, useEffect, useMemo, useRef, useCallback, useDeferredValue, lazy, Suspense } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents, GeoJSON, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MarkerCluster from './MarkerCluster';
import Login from './Login';
import UserAdmin from './UserAdmin';
import Modal, { ModalCloseButton } from './components/Modal';
import ConfirmDialog from './components/ConfirmDialog';
import Toast from './components/Toast';
import UnitPickerModal from './components/UnitPickerModal';
import DetailModal from './components/DetailModal';
import ImportModal from './components/ImportModal';
import ExportModal from './components/ExportModal';
import AssignModal from './components/AssignModal';
import PerformersModal from './components/PerformersModal';
import TermineModal from './components/TermineModal';
import Celebration from './components/Celebration';
import { IconChart, IconPencil, IconCalendar, IconTrophy, IconDots, IconNote } from './components/icons';
import { VERTRIEB_STATUS, getColor } from './statusColors';
import { VERSION } from './version';
import { apiFetch, UNAUTHORIZED_EVENT, TOKEN_RENEWED_EVENT } from './api';
import { escapeHtml, getCurrentPosition, distanceMeters, formatDistance, buildingKind } from './utils';
import { countTermineHeute, TERMINE_CHANGED_EVENT } from './terminUtils';
import { getOfflineQueueForUser, removeFromOfflineQueue } from './offlineQueue';

// Nur Admins brauchen Dashboard und Zeichenwerkzeug — aus dem Haupt-Bundle heraushalten
const Dashboard = lazy(() => import('./Dashboard'));
const DrawControl = lazy(() => import('./DrawControl'));

const API = '/api/leads';
const TERRITORIES_API = '/api/territories';
const TERMINE_API = '/api/termine';
const LEAD_PAGE_SIZE = 150; // Sidebar-Liste rendert erst diese Anzahl, Rest folgt beim Scrollen

const TERRITORY_MIN_ZOOM   = 12;
const TERRITORY_LABEL_ZOOM = TERRITORY_MIN_ZOOM + 3; // 15

function TerritoryLabels({ territories }) {
  const map = useMap();
  const layersRef = useRef(new Map());

  const applyVisibility = useCallback((z) => {
    const visible   = z >= TERRITORY_MIN_ZOOM;
    const showLabel = z >= TERRITORY_LABEL_ZOOM;
    layersRef.current.forEach(({ layer }) => {
      layer.setStyle({
        opacity:     visible ? 1    : 0,
        fillOpacity: visible ? 0.12 : 0,
      });
      if (layer.getTooltip()) {
        if (showLabel) layer.openTooltip();
        else           layer.closeTooltip();
      }
    });
  }, []);

  // Leaflet öffnet permanent-Tooltips nach dem Layer-Mount automatisch —
  // korrigiere die Sichtbarkeit nachträglich sobald alle Layer gerendert sind
  useEffect(() => {
    const t = setTimeout(() => applyVisibility(map.getZoom()), 0);
    return () => clearTimeout(t);
  }, [territories, applyVisibility, map]);

  useMapEvents({ zoomend: () => applyVisibility(map.getZoom()) });

  return territories.map(t => {
    try {
      const geo = typeof t.geojson === 'string' ? JSON.parse(t.geojson) : t.geojson;
      if (!geo) return null;
      const color     = t.color || '#6366f1';
      const z         = map.getZoom();
      const visible   = z >= TERRITORY_MIN_ZOOM;
      const showLabel = z >= TERRITORY_LABEL_ZOOM;
      return (
        <GeoJSON
          key={t.id}
          data={{ type: 'Feature', geometry: geo, properties: {} }}
          style={{ color, opacity: visible ? 1 : 0, fillOpacity: visible ? 0.12 : 0, weight: 2.5, dashArray: '6,4' }}
          onEachFeature={(_, layer) => {
            layersRef.current.set(t.id, { layer, color });
            // Name/Benutzer/Farbe stammen aus der DB — escapen, Leaflet rendert rohes HTML
            layer.bindTooltip(
              `<div class="territory-label-inner" style="border-color:${escapeHtml(color)};color:${escapeHtml(color)}">
                <span class="territory-label-name">${escapeHtml(t.name || 'Gebiet')}</span>
                <span class="territory-label-user">${escapeHtml(t.assigned_to || '—')}</span>
              </div>`,
              { permanent: true, direction: 'center', className: 'territory-tooltip' }
            );
            if (!showLabel) layer.closeTooltip();
          }}
        />
      );
    } catch { return null; }
  });
}

// Start-Ausschnitt: letzte Kartenposition des Nutzers (localStorage) — sonst einmalig
// auf die geladenen Leads zoomen statt immer auf Deutschland (Zoom 6). Danach wird
// jede Bewegung gemerkt, damit man morgens dort weitermacht, wo man abends aufgehört hat.
function MapViewMemory({ leads, username }) {
  const map = useMap();
  const initializedFor = useRef(null);
  const storageKey = username ? `crm_map_view_${username}` : null;

  useEffect(() => {
    if (!storageKey || initializedFor.current === storageKey) return;
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(storageKey)); } catch {}
    if (saved && Number.isFinite(saved.lat) && Number.isFinite(saved.lng) && Number.isFinite(saved.zoom)) {
      map.setView([saved.lat, saved.lng], saved.zoom, { animate: false });
      initializedFor.current = storageKey;
      return;
    }
    const pts = leads.filter(l => l.lat && l.lng).map(l => [l.lat, l.lng]);
    if (pts.length === 0) return; // Leads noch nicht da — beim nächsten Durchlauf
    map.fitBounds(L.latLngBounds(pts), { padding: [30, 30], maxZoom: 16, animate: false });
    initializedFor.current = storageKey;
  }, [map, leads, storageKey]);

  useMapEvents({
    moveend: () => {
      if (!storageKey || initializedFor.current !== storageKey) return;
      const c = map.getCenter();
      try { localStorage.setItem(storageKey, JSON.stringify({ lat: c.lat, lng: c.lng, zoom: map.getZoom() })); } catch {}
    },
  });
  return null;
}

function MapInvalidator({ view, sidebarCollapsed }) {
  const map = useMap();
  useEffect(() => {
    // .main steht bei view === 'dashboard' auf display:none, Leaflet cached dabei
    // die zuletzt bekannte Container-Größe (0) und rendert beim Zurückwechseln
    // erst nach diesem Aufruf wieder mit korrekter Größe statt mit Stale-Tiles.
    // Gleiches beim Ein-/Ausklappen der Sidebar (Karte wird breiter/schmaler):
    // erst nach der 280-ms-Transition messen.
    if (view === 'dashboard') return;
    const t = setTimeout(() => map.invalidateSize(), 320);
    return () => clearTimeout(t);
  }, [map, view, sidebarCollapsed]);
  return null;
}

// Gleiche Media-Query wie die Drawer-Regeln in index.css: dort ist die Sidebar ein
// ausklappbarer Drawer, sonst steht sie fest links und lässt sich nur einklappen.
const DRAWER_QUERY = '(max-width: 768px), (max-width: 1024px) and (orientation: portrait)';
function useIsDrawer() {
  const [isDrawer, setIsDrawer] = useState(() => window.matchMedia(DRAWER_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(DRAWER_QUERY);
    const onChange = () => setIsDrawer(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDrawer;
}

function LocateControl({ onError }) {
  const map = useMap();
  const layersRef = useRef([]);

  const locate = () => {
    if (!navigator.geolocation) return onError('Geolocation wird nicht unterstützt');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const { latitude: lat, longitude: lng, accuracy } = coords;

        layersRef.current.forEach(l => map.removeLayer(l));
        layersRef.current = [];

        const dot = L.divIcon({
          html: `<div style="
            width:16px;height:16px;border-radius:50%;
            background:#3b82f6;border:3px solid white;
            box-shadow:0 0 0 5px rgba(59,130,246,0.25);
            animation:pulse-dot 1.8s infinite;
          "></div>`,
          iconSize: [16, 16], iconAnchor: [8, 8], className: '',
        });

        const marker = L.marker([lat, lng], { icon: dot, zIndexOffset: 2000 });
        const circle = L.circle([lat, lng], {
          radius: accuracy, color: '#3b82f6', fillColor: '#3b82f6',
          fillOpacity: 0.08, weight: 1.5, opacity: 0.4,
        });

        marker.addTo(map);
        circle.addTo(map);
        layersRef.current = [marker, circle];
        map.flyTo([lat, lng], 17, { duration: 1.5 });
      },
      () => onError('Standort konnte nicht ermittelt werden'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div style={{ position: 'absolute', bottom: 90, right: 10, zIndex: 1000 }}>
      <button
        type="button"
        onClick={locate}
        title="Mein Standort"
        aria-label="Meinen Standort anzeigen"
        style={{
          width: 44, height: 44, borderRadius: 10, background: 'white',
          border: '1px solid #e0ddff', cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          touchAction: 'manipulation', transition: 'background 0.15s',
        }}
      >
        {/* GPS / Locate icon */}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5b21b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="3"/>
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
          <circle cx="12" cy="12" r="8" strokeOpacity="0.25"/>
        </svg>
      </button>
    </div>
  );
}

const USER_LOCATION_COLORS = ['#3b82f6','#ef4444','#16a34a','#f97316','#a855f7','#ec4899','#14b8a6'];

// Roter Zähler am Termine-Button: heutige, unerledigte Termine.
// Der Button braucht position:relative, damit der Kreis an der Ecke sitzt.
function TermineBadge({ count }) {
  if (!count) return null;
  return (
    <span aria-label={`${count} Termine heute`} style={{
      position: 'absolute', top: -4, right: -5,
      minWidth: 18, height: 18, borderRadius: 9, padding: '0 4px',
      background: '#ef4444', color: 'white', fontSize: 11, fontWeight: 800,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: '1.5px solid white', boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      lineHeight: 1,
    }}>{count > 9 ? '9+' : count}</span>
  );
}

// Server liefert nach dem Speichern den vollen Lead — has_notizen für die Sidebar ableiten
const withHasNotizen = lead => ({ ...lead, has_notizen: lead.notizen ? 1 : 0 });

const LeadItem = React.memo(function LeadItem({ lead, isSelected, onSelect, distance }) {
  const addr = [lead.strasse, lead.hausnummer].filter(Boolean).join(' ');
  const color = getColor(lead.vertrieb_status ?? null);
  return (
    <button
      type="button"
      className={`lead-item${isSelected ? ' selected' : ''}`}
      onClick={() => onSelect(lead)}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span className="lead-item-name">{addr || lead.ha_id}</span>
      </div>
      <div className="lead-item-company">{lead.ort} {lead.plz}</div>
      <div className="lead-item-meta">
        {distance != null && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#5b21b6', whiteSpace: 'nowrap' }}>{formatDistance(distance)}</span>
        )}
        <span style={{ fontSize: 11, color: '#6b7280' }}>{lead.gebaeude_typ} · {lead.anzahl_ne} NE</span>
        {lead.vertrieb_status && (
          <span className="status-badge" style={{ background: color + '22', color }}>
            {lead.vertrieb_status}{(lead.vertrieb_status_count ?? 1) > 1 && ` ×${lead.vertrieb_status_count}`}
          </span>
        )}
        {!!lead.has_notizen && <IconNote />}
      </div>
    </button>
  );
});

export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem('crm_token') || null);
  const [user, setUser]   = useState(() => {
    try {
      const raw = localStorage.getItem('crm_user');
      if (!raw || raw === 'undefined' || raw === 'null') return null;
      return JSON.parse(raw);
    } catch {
      localStorage.removeItem('crm_user');
      localStorage.removeItem('crm_token');
      return null;
    }
  });

  const [leads, setLeads] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatuses, setFilterStatuses] = useState([]); // leer = alle Status
  const [filterVertriebler, setFilterVertriebler] = useState([]); // leer = alle Vertriebler
  const [vertriebDropdown, setVertriebDropdown] = useState(false);
  const [filterGee, setFilterGee] = useState([]); // 'Ja' | 'Nein' (gee_liegt_vor), leer = alle
  const [filterTypen, setFilterTypen] = useState([]); // 'EFH' | 'MFH', leer = alle
  const [objektDropdown, setObjektDropdown] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null); // { title, message, confirmLabel, onConfirm }
  const [drawResult, setDrawResult] = useState(null);
  const [drawMode, setDrawMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // PC/Querformat: Sidebar eingeklappt? Startet immer eingeklappt — Karte zuerst,
  // Liste per Burger einblenden, wie im Hochformat (User-Wunsch). Bewusst nicht
  // gespeichert; alten Wert aus v11.6 aufräumen.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  useEffect(() => { try { localStorage.removeItem('crm_sidebar_collapsed'); } catch {} }, []);
  const isDrawer = useIsDrawer();
  const sidebarVisible = isDrawer ? sidebarOpen : !sidebarCollapsed;
  const [excelDropdown, setExcelDropdown] = useState(false);
  const [navMenu, setNavMenu] = useState(false);
  const [view, setView] = useState('map');
  const [splash, setSplash] = useState(false); // Logo-Animation direkt nach Login

  // Sidebar-Sortierung: 'naehe' (GPS-Entfernung) | 'strasse' | 'neueste' (Import-Reihenfolge)
  const [sortMode, setSortMode] = useState(() => {
    try { return localStorage.getItem('crm_sort_mode') || 'strasse'; } catch { return 'strasse'; }
  });
  const [myPos, setMyPos] = useState(null);
  const [posStatus, setPosStatus] = useState('idle'); // idle | loading | ok | error
  const requestPos = useCallback(() => {
    setPosStatus('loading');
    getCurrentPosition().then(p => { if (p) { setMyPos(p); setPosStatus('ok'); } else setPosStatus('error'); });
  }, []);
  const changeSortMode = useCallback(mode => {
    setSortMode(mode);
    try { localStorage.setItem('crm_sort_mode', mode); } catch {}
  }, []);
  // Standort erst holen, wenn eingeloggt und "Nähe" aktiv ist — kein GPS-Prompt auf der Login-Seite
  useEffect(() => { if (token && sortMode === 'naehe') requestPos(); }, [token, sortMode, requestPos]);

  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const deferredSearch = useDeferredValue(search);

  // Durchsuchbarer Text pro Lead einmal vorab berechnen statt bei jedem Tastendruck
  // erneut für alle ~14k Leads x 5 Felder zu normalisieren (toLowerCase ist der teure Teil).
  const searchIndex = useMemo(() => {
    const map = new Map();
    for (const l of leads) {
      // Straße+Hausnummer als ein zusammenhängender Block (wie in der Anzeige),
      // damit z.B. "Sulburgring 44" als Ganzes matcht statt nur "Sulburgring".
      const adresse = [l.strasse, l.hausnummer].filter(Boolean).join(' ');
      map.set(l.id, [l.ort, adresse, l.ha_id, l.plz, l.mfh_eigentuemer].filter(Boolean).join(' ␟ ').toLowerCase());
    }
    return map;
  }, [leads]);

  // Entfernungen einmal pro Standort berechnen — nicht bei jedem Tastendruck
  const distances = useMemo(() => {
    if (!myPos) return null;
    const m = new Map();
    for (const l of leads) if (l.lat && l.lng) m.set(l.id, distanceMeters(myPos, l));
    return m;
  }, [leads, myPos]);

  // Einmal sortieren, dann filtern (Filter erhält die Reihenfolge) — die Sortierung
  // über 14k Leads ist zu teuer, um sie pro Suchanschlag zu wiederholen.
  const collator = useMemo(() => new Intl.Collator('de', { numeric: true, sensitivity: 'base' }), []);
  const sortedLeads = useMemo(() => {
    if (sortMode === 'naehe' && distances) {
      return [...leads].sort((a, b) => (distances.get(a.id) ?? Infinity) - (distances.get(b.id) ?? Infinity));
    }
    if (sortMode === 'strasse') {
      const key = l => `${l.strasse || ''} ${l.hausnummer || ''}`;
      return [...leads].sort((a, b) => collator.compare(a.ort || '', b.ort || '') || collator.compare(key(a), key(b)));
    }
    return leads;
  }, [leads, sortMode, distances, collator]);

  // Zweistufig: alle Filter außer Status → Basis für die Zahlen der Status-Chips (sie
  // zeigen, was der Nutzer gerade sieht, statt immer den Gesamtbestand); dann Status.
  // Der Status-Filter selbst zählt bewusst nicht mit, sonst stünden andere Chips auf 0.
  const preFiltered = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    return sortedLeads.filter(l => {
      const matchSearch = !q || (searchIndex.get(l.id) || '').includes(q);
      const matchVertriebler = filterVertriebler.length === 0 || filterVertriebler.includes(l.assigned_to);
      const matchGee = filterGee.length === 0 || filterGee.includes(l.gee_liegt_vor);
      const matchTyp = filterTypen.length === 0 || filterTypen.includes(buildingKind(l));
      return matchSearch && matchVertriebler && matchGee && matchTyp;
    });
  }, [sortedLeads, deferredSearch, filterVertriebler, filterGee, filterTypen, searchIndex]);

  const filtered = useMemo(() => (
    filterStatuses.length === 0
      ? preFiltered
      : preFiltered.filter(l => filterStatuses.includes(l.vertrieb_status ?? null))
  ), [preFiltered, filterStatuses]);

  const assignedUsers = useMemo(
    () => [...new Set(leads.map(l => l.assigned_to).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [leads]
  );

  const toggleStatusFilter = (value) => {
    setFilterStatuses(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const toggleTypFilter = (typ) => {
    setFilterTypen(prev => prev.includes(typ) ? prev.filter(v => v !== typ) : [...prev, typ]);
  };
  const toggleGeeFilter = (wert) => {
    setFilterGee(prev => prev.includes(wert) ? prev.filter(v => v !== wert) : [...prev, wert]);
  };
  const objektFilterCount = filterGee.length + filterTypen.length;

  const toggleVertriebFilter = (username) => {
    setFilterVertriebler(prev => prev.includes(username) ? prev.filter(v => v !== username) : [...prev, username]);
  };

  // ── Browser-History: Zurück-Taste schließt Modal/Dashboard statt App zu verlassen ──
  const modalStateRef = useRef(null);
  const viewStateRef = useRef('map');
  useEffect(() => { modalStateRef.current = modal; }, [modal]);
  useEffect(() => { viewStateRef.current = view; }, [view]);
  const overlayPushedRef = useRef(false);
  const viewPushedRef = useRef(false);

  const clearOverlay = useCallback(() => {
    setModal(null);
    setSelectedUnit(null);
    setDrawResult(prev => { prev?.drawnItems?.clearLayers(); return null; });
  }, []);

  const openModal = useCallback((name) => {
    if (!overlayPushedRef.current) {
      window.history.pushState({ crmOverlay: true }, '');
      overlayPushedRef.current = true;
    }
    setModal(name);
  }, []);

  const closeModal = useCallback(() => {
    if (overlayPushedRef.current) {
      // sofort zurücksetzen — ein Doppel-Schließen darf nicht zweimal history.back() auslösen
      overlayPushedRef.current = false;
      window.history.back();   // popstate-Handler räumt den State auf
    } else {
      clearOverlay();
    }
  }, [clearOverlay]);

  useEffect(() => {
    const onPop = () => {
      if (modalStateRef.current) {
        overlayPushedRef.current = false;
        clearOverlay();
      } else if (viewStateRef.current === 'dashboard') {
        viewPushedRef.current = false;
        setView('map');
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [clearOverlay]);

  const toggleDashboard = useCallback(() => {
    if (viewStateRef.current === 'dashboard') {
      if (viewPushedRef.current) window.history.back();
      else setView('map');
    } else {
      window.history.pushState({ crmView: 'dashboard' }, '');
      viewPushedRef.current = true;
      setDrawMode(false); // Zeichenmodus ist nur auf der Karte sinnvoll
      setSidebarOpen(false);
      setView('dashboard');
    }
  }, []);

  const [userLocations, setUserLocations] = useState([]);

  // Eigenen Standort alle 2 Minuten senden (nur normale User)
  useEffect(() => {
    if (!token || user?.role === 'admin') return;
    const send = () => {
      navigator.geolocation?.getCurrentPosition(({ coords }) => {
        // Nebenbei die Nähe-Sortierung aktuell halten (alle 2 Minuten)
        setMyPos({ lat: coords.latitude, lng: coords.longitude });
        setPosStatus('ok');
        apiFetch('/api/auth/location', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader },
          body: JSON.stringify({ lat: coords.latitude, lng: coords.longitude }),
        });
      });
    };
    send();
    const interval = setInterval(send, 120000);
    return () => clearInterval(interval);
  }, [token, authHeader]);

  // Admin: User-Standorte alle 30 Sekunden laden
  useEffect(() => {
    if (!token || user?.role !== 'admin') return;
    const load = () => {
      if (document.hidden) return; // Tab im Hintergrund/Display aus: kein Abruf
      apiFetch('/api/auth/locations', { headers: authHeader })
        .then(r => r.json()).then(setUserLocations).catch(() => {});
    };
    load();
    const interval = setInterval(load, 30000);
    document.addEventListener('visibilitychange', load); // beim Zurückkehren sofort aktualisieren
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', load);
    };
  }, [token, authHeader, user]);

  // view zurücksetzen — sonst bleibt ein vom vorherigen Nutzer offenes Dashboard
  // stehen, wenn danach ein normaler User einloggt: der hat keinen Umschalter-Button
  // zurück zur Karte (admin-only) und sieht nur ein leeres Dashboard ohne Ausweg.
  const handleLogin = (u, t) => { setUser(u); setToken(t); setSplash(true); setView('map'); };

  // Logo-Splash nach Login: nach 2s wieder ausblenden (Animation läuft rein in CSS,
  // endet bei 1,8s — der Puffer verhindert ein abruptes Ende)
  useEffect(() => {
    if (!splash) return;
    const t = setTimeout(() => setSplash(false), 2000);
    return () => clearTimeout(t);
  }, [splash]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    setToken(null); setUser(null); setLeads([]); setView('map');
    // UI-Zustand des bisherigen Nutzers verwerfen — sonst öffnet nach 401-Logout und
    // Login eines anderen Nutzers am selben Gerät dessen DetailModal/Filter wieder
    clearOverlay(); setSelected(null); setConfirm(null); setSearch('');
    setFilterStatuses([]); setFilterVertriebler([]); setFilterGee([]); setFilterTypen([]);
    overlayPushedRef.current = false; viewPushedRef.current = false;
  }, [clearOverlay]);

  // Abgelaufene Sitzung: jede 401-Antwort loggt global aus,
  // statt dass einzelne Saves nur generisch fehlschlagen
  useEffect(() => {
    const onUnauthorized = () => {
      handleLogout();
      setToast({ message: 'Sitzung abgelaufen — bitte erneut anmelden.', type: 'error' });
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [handleLogout]);

  // Tabs synchron halten: Logout/Login in einem Tab übernehmen alle anderen — sonst
  // zeigt ein liegengebliebener Tab weiter die Leads des vorherigen Nutzers (und würde
  // dessen Sitzung ohne den Guard in apiFetch per Renewal sogar zurückschreiben)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== null && e.key !== 'crm_token' && e.key !== 'crm_user') return;
      const t = localStorage.getItem('crm_token');
      if (!t) { handleLogout(); return; }
      if (t === token) return;
      let nextUser = null;
      try { nextUser = JSON.parse(localStorage.getItem('crm_user')); } catch {}
      if (!nextUser) { handleLogout(); return; }
      if (nextUser.id !== user?.id) {
        setUser(nextUser);
        setLeads([]); // nicht die Leads des vorherigen Nutzers zeigen, bis neu geladen ist
      }
      setToken(t); // stößt über den Lade-Effekt das Neuladen der Daten an
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [token, user, handleLogout]);

  // Sliding Renewal: Server schickt bei bald ablaufendem Token ein frisches mit —
  // in den State übernehmen, damit auch laufende Intervalle das neue Token nutzen
  useEffect(() => {
    const onRenewed = e => setToken(e.detail);
    window.addEventListener(TOKEN_RENEWED_EVENT, onRenewed);
    return () => window.removeEventListener(TOKEN_RENEWED_EVENT, onRenewed);
  }, []);

  // Bedingtes Laden: der Server (Express) setzt einen ETag und antwortet bei unverändertem
  // Stand mit 304 — die Liste wird bei jedem Sichtbarwerden nachgefragt, kostet dann aber
  // nichts (Admin: 19k Leads ≈ 750 KB gzip pro Abruf). Der Browser-Cache hilft hier nicht,
  // weil /api mit no-store ausgeliefert wird — daher If-None-Match von Hand.
  const leadsEtagRef = useRef(null); // { token, etag }
  // Aktuelles Token für async-Antworten: eine späte Antwort nach Logout/Nutzerwechsel
  // darf nicht die Leads des vorherigen Nutzers zurückschreiben
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const load = useCallback(async () => {
    try {
      const etag = leadsEtagRef.current?.token === token ? leadsEtagRef.current.etag : null;
      const res = await apiFetch(API, { headers: { ...authHeader, ...(etag ? { 'If-None-Match': etag } : {}) } });
      if (tokenRef.current !== token) return; // Token inzwischen gewechselt (Logout/Renewal → lädt neu)
      if (res.status === 304) return; // unverändert seit dem letzten Abruf
      if (res.status === 401) return; // globaler 401-Handler übernimmt
      if (!res.ok) return;
      const text = await res.text();
      if (tokenRef.current !== token) return;
      if (text) setLeads(JSON.parse(text));
      leadsEtagRef.current = { token, etag: res.headers.get('ETag') };
    } catch (e) { console.error('load error:', e); }
  }, [authHeader, token]);

  // Speicherungen nachholen, die mangels Verbindung in die Offline-Queue gewandert sind
  // (siehe offlineQueue.js) — wird bei "online", Tab-Sichtbarkeit und Start aufgerufen.
  const flushOfflineQueue = useCallback(async () => {
    if (!user?.username) return;
    const queue = getOfflineQueueForUser(user.username);
    if (queue.length === 0) return;
    for (const item of queue) {
      let res;
      try {
        res = await apiFetch(`${API}/${item.leadId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeader },
          body: JSON.stringify(item.body),
        });
      } catch {
        break; // weiterhin offline — beim nächsten Versuch erneut probieren
      }
      // Abgelaufene Sitzung: apiFetch loggt global aus (UNAUTHORIZED_EVENT) — Eintrag
      // in der Queue lassen, nach dem Re-Login versucht es der Start-Effekt erneut.
      if (res.status === 401) break;
      if (res.status === 409) {
        removeFromOfflineQueue(item.username, item.leadId);
        setToast({ message: 'Offline-Änderung konnte nicht übernommen werden (zwischenzeitlich geändert) — bitte Eintrag prüfen.', type: 'error' });
        continue;
      }
      // 5xx: vermutlich vorübergehend (Server-Neustart, Wartung) — später erneut versuchen.
      // 4xx (außer 401/409): würde bei jedem Versuch gleich fehlschlagen, daher nicht endlos wiederholen.
      if (res.status >= 400 && res.status < 500) {
        removeFromOfflineQueue(item.username, item.leadId);
        setToast({ message: 'Offline-Änderung konnte nicht gespeichert werden — bitte Eintrag manuell prüfen.', type: 'error' });
        continue;
      }
      if (!res.ok) continue; // 5xx — beim nächsten Versuch erneut probieren
      const saved = withHasNotizen(await res.json());
      removeFromOfflineQueue(item.username, item.leadId);
      setLeads(prev => prev.map(l => l.id === saved.id ? saved : l));
      setToast({ message: 'Offline-Änderung synchronisiert' });
    }
  }, [authHeader, user?.username]);

  // load() (volles GET, überschreibt den kompletten leads-State) und flushOfflineQueue()
  // (patcht einzelne Leads) dürfen NIE parallel laufen — sonst kann load() mit einem vor
  // dem Sync geladenen Snapshot eine gerade erst synchronisierte Offline-Änderung wieder
  // zurücksetzen, je nachdem welcher Request zuerst antwortet. syncLeads() serialisiert
  // beide über eine Kette (syncChainRef), egal ob von "online", Tab-Sichtbarkeit oder dem
  // Start-Effekt ausgelöst — ein späterer Aufruf wartet immer auf den vorherigen.
  const syncChainRef = useRef(Promise.resolve());
  const syncLeads = useCallback(() => {
    const next = syncChainRef.current.catch(() => {}).then(load).then(flushOfflineQueue);
    syncChainRef.current = next;
    return next;
  }, [load, flushOfflineQueue]);

  useEffect(() => {
    window.addEventListener('online', syncLeads);
    return () => window.removeEventListener('online', syncLeads);
  }, [syncLeads]);

  const loadTerritories = useCallback(async () => {
    if (user?.role !== 'admin') return;
    try {
      const res = await apiFetch(TERRITORIES_API, { headers: authHeader });
      if (!res.ok) return;
      const text = await res.text();
      if (text) setTerritories(JSON.parse(text));
    } catch (e) { console.error('loadTerritories error:', e); }
  }, [authHeader, user]);

  const loadUsers = useCallback(async () => {
    if (user?.role !== 'admin') return;
    try {
      const res = await apiFetch('/api/auth/users', { headers: authHeader });
      if (!res.ok) return;
      const text = await res.text();
      if (text) setAllUsers(JSON.parse(text));
    } catch (e) { console.error('loadUsers error:', e); }
  }, [authHeader, user]);

  // Heutige offene Termine für den roten Navbar-Badge
  const [termineHeute, setTermineHeute] = useState(0);
  const loadTermineHeute = useCallback(async () => {
    try {
      const res = await apiFetch(TERMINE_API, { headers: authHeader });
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) setTermineHeute(countTermineHeute(data));
    } catch {}
  }, [authHeader]);

  // Badge aktuell halten, wenn Termine angelegt/erledigt/gelöscht werden
  useEffect(() => {
    window.addEventListener(TERMINE_CHANGED_EVENT, loadTermineHeute);
    return () => window.removeEventListener(TERMINE_CHANGED_EVENT, loadTermineHeute);
  }, [loadTermineHeute]);

  useEffect(() => {
    if (token) Promise.all([syncLeads(), loadTerritories(), loadUsers(), loadTermineHeute()]);
  }, [token, syncLeads, loadTerritories, loadUsers, loadTermineHeute]);

  // Leads + Termin-Badge aktualisieren, sobald die App wieder sichtbar wird (Tab-Wechsel,
  // Display an) — so sieht man Änderungen der Kollegen ohne Neu-Login, und der Badge
  // stimmt auch nach Mitternacht noch; gleichzeitig ein guter Moment, um eine
  // wartende Offline-Speicherung nachzuholen (Display aus war evtl. das Funkloch)
  useEffect(() => {
    if (!token) return;
    const onVisible = () => { if (!document.hidden) { syncLeads(); loadTermineHeute(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [token, syncLeads, loadTermineHeute]);

  const handleDrawComplete = useCallback((result) => {
    setDrawResult(result);
    openModal('assign');
  }, [openModal]);

  // Die Modals brauchen (und speichern!) den kompletten Datensatz — ohne Volldaten würde PUT
  // Felder leeren. Vertriebler bekommen die Liste bereits mit Volldaten, damit Öffnen und
  // Speichern auch im Funkloch funktionieren (Offline-Queue); die Admin-Liste ist aus
  // Bandbreitengründen schlank, dort werden die Volldaten per GET /:id nachgeladen.
  const openLead = useCallback(async (lead, modalName) => {
    if (user?.role !== 'admin') {
      setSelected(lead);
      setSelectedUnit(null);
      openModal(modalName);
      setSidebarOpen(false);
      return;
    }
    try {
      const res = await apiFetch(`${API}/${lead.id}`, { headers: authHeader });
      if (!res.ok) throw new Error(`Fehler ${res.status}`);
      setSelected(await res.json());
      setSelectedUnit(null);
      openModal(modalName);
      setSidebarOpen(false);
    } catch (e) {
      console.error('openLead error:', e);
      setToast({ message: 'Lead konnte nicht geladen werden — bitte erneut versuchen.', type: 'error' });
    }
  }, [authHeader, openModal, user]);

  // MFH immer über den Einheiten-Picker — Sidebar und Marker öffnen dasselbe Modal,
  // sonst entstehen zwei Status-Ebenen für dasselbe Haus (Gebäude vs. Einheiten)
  const handleSelect = useCallback(lead => {
    openLead(lead, (lead.anzahl_ne ?? 0) >= 2 ? 'units' : 'detail');
  }, [openLead]);

  const handleHouseClick = useCallback(lead => { openLead(lead, 'units'); }, [openLead]);

  const handleUnitSaved = useCallback(updatedLead => {
    const norm = withHasNotizen(updatedLead);
    setLeads(prev => prev.map(l => l.id === norm.id ? norm : l));
    setSelected(norm);
    closeModal();
    setToast(norm._pendingSync
      ? { message: 'Kein Netz — wird automatisch synchronisiert, sobald wieder Verbindung besteht.', type: 'info' }
      : { message: 'Erfolgreich gespeichert' });
  }, [closeModal]);

  // wirft bei Fehler — AssignModal fängt ab und zeigt die Meldung im Modal
  const handleAssign = async ({ geoJson, lead_ids, assigned_to, name }) => {
    const res = await apiFetch(TERRITORIES_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader },
      body: JSON.stringify({ name, geojson: geoJson, assigned_to, lead_ids }),
    });
    if (!res.ok) throw new Error(`Fehler ${res.status}`);
    await load();
    await loadTerritories();
    closeModal();
    setToast({ message: 'Gebiet zugewiesen' });
  };

  // Beim Löschen entscheidet der Admin, ob die Leads im Gebiet ihre Zuweisung behalten
  // oder freigegeben werden — vorher behauptete der Dialog "bleiben zugewiesen", während
  // das Backend sie freigab.
  const handleDeleteTerritory = (t) => {
    const doDelete = async (release) => {
      setConfirm(null);
      try {
        const res = await apiFetch(`${TERRITORIES_API}/${t.id}${release ? '?release=1' : ''}`, { method: 'DELETE', headers: authHeader });
        if (!res.ok) throw new Error(`Fehler ${res.status}`);
        const data = await res.json();
        loadTerritories();
        if (release) load(); // assigned_to hat sich geändert — Karte/Sidebar nachziehen
        setToast({ message: release
          ? `Gebiet gelöscht — ${data.released ?? 0} Leads freigegeben`
          : 'Gebiet gelöscht — Zuweisungen bleiben bestehen' });
      } catch (err) {
        console.error('Fehler beim Löschen:', err);
        setToast({ message: 'Löschen fehlgeschlagen — bitte erneut versuchen.', type: 'error' });
      }
    };
    const name = `„${t.name || 'Gebiet'}“`;
    if (!t.assigned_to) {
      setConfirm({ title: 'Gebiet löschen?', message: `${name} ist niemandem zugewiesen und wird gelöscht.`, onConfirm: () => doDelete(false) });
      return;
    }
    setConfirm({
      title: 'Gebiet löschen?',
      message: `${name} (zugewiesen an ${t.assigned_to}) wird gelöscht.\nWas soll mit den Leads im Gebiet passieren?\n\n„Leads freigeben“ nimmt ${t.assigned_to} die Leads in diesem Gebiet weg — Leads, die auch in einem anderen Gebiet von ${t.assigned_to} liegen, bleiben zugewiesen.`,
      confirmLabel: 'Leads freigeben',
      onConfirm: () => doDelete(true),
      secondaryLabel: 'Zuweisung behalten',
      onSecondary: () => doDelete(false),
    });
  };

  // Sidebar-Liste stückweise rendern: bei jedem Tastendruck nur die ersten
  // LEAD_PAGE_SIZE Treffer mounten statt alle (bis zu 14k) DOM-Knoten neu
  // aufzubauen — der eigentliche Grund fürs Einfrieren bei breiten Suchbegriffen.
  // Beim Scrollen ans Ende lädt der Sentinel per IntersectionObserver mehr nach.
  const leadListRef = useRef(null);
  // Sentinel als Callback-Ref im State: wird er neu gemountet (z.B. nach Sortierwechsel),
  // beobachtet der Observer den neuen Knoten statt eines abgehängten
  const [leadListSentinel, setLeadListSentinel] = useState(null);
  const [leadPage, setLeadPage] = useState(LEAD_PAGE_SIZE);

  // Nur bei echter Änderung von Suche/Filtern/Sortierung zurücksetzen — nicht bei jedem
  // Neuladen/Speichern (neues filtered-Array), sonst springt die Liste auf 150 zurück
  useEffect(() => { setLeadPage(LEAD_PAGE_SIZE); },
    [deferredSearch, filterStatuses, filterVertriebler, filterGee, filterTypen, sortMode]);

  const filteredCount = filtered.length;
  useEffect(() => {
    const root = leadListRef.current;
    if (!root || !leadListSentinel) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setLeadPage(p => Math.min(p + LEAD_PAGE_SIZE, filteredCount));
    }, { root, rootMargin: '400px 0px' });
    io.observe(leadListSentinel);
    return () => io.disconnect();
  }, [leadListSentinel, leadPage, filteredCount]); // leadPage: neu beobachten, falls Sentinel sichtbar bleibt

  const visibleLeads = useMemo(() => filtered.slice(0, leadPage), [filtered, leadPage]);

  const leadListItems = useMemo(() =>
    visibleLeads.map(lead => (
      <LeadItem
        key={lead.id}
        lead={lead}
        isSelected={selected?.id === lead.id}
        onSelect={handleSelect}
        distance={sortMode === 'naehe' ? distances?.get(lead.id) : undefined}
      />
    )), [visibleLeads, selected?.id, handleSelect, sortMode, distances]);

  // Hooks dürfen nie nach einem bedingten Return stehen — sonst ändert sich die
  // Hook-Anzahl beim Login/Logout-Übergang und React crasht (weißer Bildschirm)
  const stats = useMemo(() => {
    const counts = {};
    for (const lead of preFiltered) {
      const s = lead.vertrieb_status ?? null;
      counts[s] = (counts[s] ?? 0) + 1;
    }
    return counts;
  }, [preFiltered]);

  if (!token || !user) return (
    <>
      <Login onLogin={handleLogin} />
      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}
    </>
  );

  const handleSave = updated => {
    const norm = withHasNotizen(updated);
    setLeads(prev => prev.map(l => l.id === norm.id ? norm : l));
    setSelected(prev => ({ ...prev, ...norm }));
    closeModal();
    setToast(norm._pendingSync
      ? { message: 'Kein Netz — wird automatisch synchronisiert, sobald wieder Verbindung besteht.', type: 'info' }
      : { message: 'Erfolgreich gespeichert' });
  };

  const handleDelete = lead => {
    setConfirm({
      title: 'Eintrag löschen?',
      message: `${lead.ha_id} wird unwiderruflich gelöscht.`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          const res = await apiFetch(`${API}/${lead.id}`, { method: 'DELETE', headers: authHeader });
          if (!res.ok) throw new Error(`Fehler ${res.status}`);
          setLeads(prev => prev.filter(l => l.id !== lead.id));
          setSelected(null);
          closeModal();
          setToast({ message: 'Eintrag gelöscht' });
        } catch (err) {
          console.error('Fehler beim Löschen:', err);
          setToast({ message: 'Löschen fehlgeschlagen — bitte erneut versuchen.', type: 'error' });
        }
      },
    });
  };

  return (
    <div className={`app${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
      <nav className="navbar">
        {/* Seitenleiste und Zeichnen gehören zur Karte — im Dashboard ausblenden */}
        {view !== 'dashboard' && (
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => (isDrawer ? setSidebarOpen(v => !v) : setSidebarCollapsed(v => !v))}
          aria-label={sidebarVisible ? 'Seitenleiste ausblenden' : 'Seitenleiste einblenden'}
          aria-expanded={sidebarVisible}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        )}
        <img src="/logo.png" alt="Futuris Sales" className="navbar-logo" />
        <span className="navbar-title" style={{ color: 'white', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap' }}>Vertriebspartner <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>v{VERSION}</span></span>
        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', marginLeft: 'auto' }}>{leads.length} gesamt</span>
        {user?.role === 'admin' && (
          <button className="nav-hide-portrait" onClick={() => openModal('users')}>Benutzer</button>
        )}
        {user?.role === 'admin' && (
          <button
            className="nav-hide-portrait"
            onClick={toggleDashboard}
            aria-pressed={view === 'dashboard'}
            style={view === 'dashboard' ? { background: 'rgba(255,255,255,0.42)', border: '1px solid white', fontWeight: 700 } : {}}
          >
            <IconChart /> {view === 'dashboard' ? 'Karte' : 'Dashboard'}
          </button>
        )}
        {user?.role !== 'admin' && (
          <button className="nav-hide-portrait" onClick={() => openModal('performers')}><IconTrophy /> Performer</button>
        )}
        <button className="nav-hide-portrait" style={{ position: 'relative' }} onClick={() => openModal('termine')}>
          <IconCalendar /> Termine
          <TermineBadge count={termineHeute} />
        </button>
        {user?.role === 'admin' && (
          <button className="nav-hide-portrait" onClick={() => openModal('territories')}>Gebiete ({territories.length})</button>
        )}
        {user?.role === 'admin' && view !== 'dashboard' && (
          <button
            className="nav-hide-portrait"
            onClick={() => setDrawMode(v => !v)}
            aria-pressed={drawMode}
            style={drawMode ? { background: 'rgba(255,255,255,0.42)', border: '1px solid white', fontWeight: 700 } : {}}
          >
            <IconPencil /> {drawMode ? 'Aktiv' : 'Zeichnen'}
          </button>
        )}
        {user?.role === 'admin' && (
          <div className="excel-dropdown-wrap">
            <button onClick={() => setExcelDropdown(v => !v)} aria-expanded={excelDropdown}>
              Excel ▾
            </button>
            {excelDropdown && (
              <>
                <div className="excel-dropdown-backdrop" onClick={() => setExcelDropdown(false)} />
                <div className="excel-dropdown">
                  <button onClick={() => { setExcelDropdown(false); openModal('import'); }}>
                    Importieren
                  </button>
                  <button onClick={() => { setExcelDropdown(false); openModal('export'); }}>
                    Exportieren
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {/* Mehr-Menü: macht die per CSS ausgeblendeten Navbar-Aktionen auf kleinen Screens erreichbar */}
        <div className="nav-more-wrap excel-dropdown-wrap">
          <button onClick={() => setNavMenu(v => !v)} aria-label="Weitere Optionen" aria-expanded={navMenu} style={{ padding: '7px 10px', position: 'relative' }}>
            <IconDots />
            <TermineBadge count={termineHeute} />
          </button>
          {navMenu && (
            <>
              <div className="excel-dropdown-backdrop" onClick={() => setNavMenu(false)} />
              <div className="excel-dropdown">
                {user?.role === 'admin' && (
                  <>
                    <button onClick={() => { setNavMenu(false); openModal('users'); }}>Benutzer</button>
                    <button onClick={() => { setNavMenu(false); toggleDashboard(); }}>
                      {view === 'dashboard' ? 'Karte' : 'Dashboard'}
                    </button>
                    <button onClick={() => { setNavMenu(false); openModal('territories'); }}>Gebiete ({territories.length})</button>
                    {view !== 'dashboard' && (
                      <button onClick={() => { setNavMenu(false); setDrawMode(v => !v); }}>
                        {drawMode ? 'Zeichnen beenden' : 'Zeichnen'}
                      </button>
                    )}
                  </>
                )}
                {user?.role !== 'admin' && (
                  <button onClick={() => { setNavMenu(false); openModal('performers'); }}>Performer</button>
                )}
                <button onClick={() => { setNavMenu(false); openModal('termine'); }}>
                  Termine
                  {termineHeute > 0 && (
                    <span style={{ color: '#ef4444', fontWeight: 800 }}> ({termineHeute > 9 ? '9+' : termineHeute})</span>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
        <div className="navbar-user">
          <span>{user?.username}</span>
          <button onClick={handleLogout} className="btn-logout">Abmelden</button>
        </div>
      </nav>

      {/* Status- und Vertriebler-Filter-Leiste (Mehrfachauswahl) — nur auf der Karte relevant */}
      <div className="legend" style={{ display: view === 'dashboard' ? 'none' : undefined }}>
        <div
          className="legend-scroll"
          role="group"
          aria-label="Nach Status filtern"
          // Zeile ist einzeilig + seitlich scrollbar; mit der Maus gibt es kein Wischen,
          // also das normale (vertikale) Mausrad in seitliches Scrollen umlenken
          onWheel={e => {
            const el = e.currentTarget;
            // deltaMode 1 = Zeilen (Firefox, deltaY≈3), 2 = Seiten → in Pixel umrechnen
            const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientWidth : 1;
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && el.scrollWidth > el.clientWidth) el.scrollLeft += e.deltaY * factor;
          }}
        >
          <button
            type="button"
            className={`legend-item${filterStatuses.length === 0 ? ' active' : ''}`}
            aria-pressed={filterStatuses.length === 0}
            onClick={() => setFilterStatuses([])}
          >Alle ({preFiltered.length})</button>
          {VERTRIEB_STATUS.map(s => {
            const active = filterStatuses.includes(s.value ?? null);
            return (
              <button
                type="button"
                key={s.value ?? 'null'}
                className={`legend-item${active ? ' active' : ''}`}
                aria-pressed={active}
                onClick={() => toggleStatusFilter(s.value ?? null)}
              >
                <span className="legend-dot" style={{ background: s.color }} />
                {s.label} ({stats[s.value] || 0})
              </button>
            );
          })}
        </div>

        {/* Objekt-Filter (GEE, EFH/MFH) — für alle Rollen, Vertriebler planen damit ihre Runde */}
        <div className="excel-dropdown-wrap filter-dropdown-wrap legend-fixed">
          <button
            type="button"
            className={`legend-item${objektFilterCount > 0 ? ' active' : ''}`}
            aria-expanded={objektDropdown}
            onClick={() => setObjektDropdown(v => !v)}
          >
            Objekt{objektFilterCount > 0 ? ` (${objektFilterCount})` : ''} ▾
          </button>
          {objektDropdown && (
            <>
              <div className="excel-dropdown-backdrop" onClick={() => setObjektDropdown(false)} />
              <div className="excel-dropdown filter-dropdown" role="group" aria-label="Nach Objekt filtern">
                <button
                  type="button"
                  className="filter-dropdown-reset"
                  onClick={() => { setFilterGee([]); setFilterTypen([]); }}
                >
                  Alle anzeigen
                </button>
                {/* GEE ist in der DB nur Ja/Nein/leer — leere Felder fallen unter keinen der beiden */}
                {[['Ja', 'GEE vorhanden'], ['Nein', 'Ohne GEE']].map(([wert, label]) => (
                  <label key={wert} className="filter-dropdown-option">
                    <input type="checkbox" checked={filterGee.includes(wert)} onChange={() => toggleGeeFilter(wert)} />
                    {label}
                  </label>
                ))}
                {['EFH', 'MFH'].map(t => (
                  <label key={t} className="filter-dropdown-option">
                    <input type="checkbox" checked={filterTypen.includes(t)} onChange={() => toggleTypFilter(t)} />
                    {t}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        {user?.role === 'admin' && assignedUsers.length > 0 && (
          <div className="excel-dropdown-wrap filter-dropdown-wrap legend-fixed">
            <button
              type="button"
              className={`legend-item${filterVertriebler.length > 0 ? ' active' : ''}`}
              aria-expanded={vertriebDropdown}
              onClick={() => setVertriebDropdown(v => !v)}
            >
              Vertriebler{filterVertriebler.length > 0 ? ` (${filterVertriebler.length})` : ''} ▾
            </button>
            {vertriebDropdown && (
              <>
                <div className="excel-dropdown-backdrop" onClick={() => setVertriebDropdown(false)} />
                <div className="excel-dropdown filter-dropdown" role="group" aria-label="Nach Vertriebler filtern">
                  <button
                    type="button"
                    className="filter-dropdown-reset"
                    onClick={() => setFilterVertriebler([])}
                  >
                    Alle anzeigen
                  </button>
                  {assignedUsers.map(u => (
                    <label key={u} className="filter-dropdown-option">
                      <input
                        type="checkbox"
                        checked={filterVertriebler.includes(u)}
                        onChange={() => toggleVertriebFilter(u)}
                      />
                      {u}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {view === 'dashboard' && user?.role === 'admin' && (
        <div className="dash-view" style={{ flex: 1, overflowY: 'auto', background: '#f5f3ff' }}>
          <Suspense fallback={<p style={{ textAlign: 'center', color: '#6b7280', padding: 40 }}>Lädt…</p>}>
            <Dashboard authHeader={authHeader} />
          </Suspense>
        </div>
      )}

      <div className="main" style={{ display: (view === 'dashboard' && user?.role === 'admin') ? 'none' : undefined }}>
        <div className={`sidebar-overlay${sidebarOpen ? ' visible' : ''}`} onClick={() => setSidebarOpen(false)} />
        <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
          <div className="sidebar-header">
            <input
              placeholder="Suchen (Ort, Straße, HA-ID...)"
              aria-label="Leads suchen"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{filtered.length}</span>
          </div>
          <div className="sort-bar" role="group" aria-label="Sortierung der Liste">
            {[['naehe', 'Nähe'], ['strasse', 'Straße'], ['neueste', 'Neueste']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`sort-btn${sortMode === key ? ' active' : ''}`}
                aria-pressed={sortMode === key}
                onClick={() => changeSortMode(key)}
              >{label}</button>
            ))}
            {sortMode === 'naehe' && posStatus === 'loading' && <span className="sort-hint">Standort wird ermittelt…</span>}
            {sortMode === 'naehe' && posStatus === 'error' && <span className="sort-hint">Kein Standort verfügbar</span>}
          </div>
          <div className="lead-list" ref={leadListRef}>
            {filtered.length === 0 && <div className="no-leads">Keine Einträge</div>}
            {leadListItems}
            {leadPage < filtered.length && <div ref={setLeadListSentinel} aria-hidden="true" />}
          </div>
        </aside>

        <div className="map-container">
          <MapContainer center={[51.5, 9.0]} zoom={6} minZoom={2} maxZoom={19} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={21}
              maxNativeZoom={19}
            />
            <MapInvalidator view={view} sidebarCollapsed={sidebarCollapsed} />
            <MapViewMemory leads={leads} username={user?.username} />
            <LocateControl onError={msg => setToast({ message: msg, type: 'error' })} />
            <MarkerCluster
              leads={filtered}
              selected={selected}
              onSelect={handleSelect}
              onHouseClick={handleHouseClick}
            />
            {user?.role === 'admin' && drawMode && (
              <Suspense fallback={null}>
                <DrawControl leads={leads} onDrawComplete={handleDrawComplete} />
              </Suspense>
            )}
            {user?.role === 'admin' && userLocations.map((u, i) => {
              if (!u.lat || !u.lng) return null;
              const color = USER_LOCATION_COLORS[i % USER_LOCATION_COLORS.length];
              const icon = L.divIcon({
                html: `<div style="
                  width:16px;height:16px;border-radius:50%;
                  background:${color};border:3px solid white;
                  box-shadow:0 0 0 5px ${color}44;
                  animation:pulse-dot 1.8s infinite;
                "></div>`,
                iconSize: [16, 16], iconAnchor: [8, 8], className: '',
              });
              const time = new Date(u.last_seen + 'Z').toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
              return (
                <Marker key={`loc-${u.username}`} position={[u.lat, u.lng]} icon={icon} zIndexOffset={1000}>
                  <Tooltip permanent direction="top" offset={[0, -10]}>
                    <span style={{ fontWeight: 700, color }}>{u.username}</span><br />
                    <span style={{ fontSize: 11, color: '#6b7280' }}>zuletzt aktiv: {time}</span>
                  </Tooltip>
                </Marker>
              );
            })}
            {user?.role === 'admin' && <TerritoryLabels territories={territories} />}
          </MapContainer>
        </div>
      </div>

      {modal === 'import' && (
        <ImportModal onClose={closeModal} onImported={() => { load(); closeModal(); }} onDataChanged={load} authHeader={authHeader} />
      )}
      {modal === 'export' && user?.role === 'admin' && (
        <ExportModal leads={leads} authHeader={authHeader} onClose={closeModal} />
      )}
      {modal === 'units' && selected && (
        <UnitPickerModal
          lead={selected}
          onSaved={handleUnitSaved}
          onOpenDetail={() => setModal('detail')}
          onClose={closeModal}
          authHeader={authHeader}
          user={user}
        />
      )}
      {modal === 'detail' && selected && (
        <DetailModal
          lead={selected}
          selectedUnit={selectedUnit}
          onClose={closeModal}
          onSave={handleSave}
          onDelete={handleDelete}
          onOpenUnits={() => setModal('units')}
          authHeader={authHeader}
          user={user}
        />
      )}
      {modal === 'users' && user?.role === 'admin' && (
        <UserAdmin token={token} onClose={() => { closeModal(); loadUsers(); }} />
      )}
      {modal === 'assign' && drawResult && (
        <AssignModal
          drawResult={drawResult}
          users={allUsers}
          onAssign={handleAssign}
          onCancel={closeModal}
        />
      )}
      {modal === 'performers' && (
        <PerformersModal onClose={closeModal} authHeader={authHeader} currentUser={user} />
      )}
      {modal === 'termine' && (
        <TermineModal onClose={closeModal} authHeader={authHeader} currentUser={user} />
      )}
      {modal === 'territories' && user?.role === 'admin' && (
        <Modal onClose={closeModal} label="Gebiete">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2>Gebiete ({territories.length})</h2>
              <ModalCloseButton onClose={closeModal} />
            </div>
            {territories.length === 0 && <p style={{ color: '#6b7280', fontSize: 13 }}>Noch keine Gebiete gezeichnet.</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {territories.map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f9f8ff', border: '1px solid #ede9fe', borderRadius: 10, padding: '10px 14px' }}>
                  <span style={{ width: 14, height: 14, borderRadius: 3, background: t.color || '#6366f1', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1e1b4b' }}>{t.name || '—'}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Zugewiesen an: <strong style={{ color: '#374151' }}>{t.assigned_to || '—'}</strong></div>
                  </div>
                  <div style={{ background: '#ede9fe', color: '#5b21b6', fontWeight: 700, fontSize: 13, borderRadius: 8, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                    {t.lead_count ?? 0} Leads
                  </div>
                  <button className="btn btn-danger" style={{ fontSize: 12, padding: '6px 12px', minHeight: 36 }} onClick={() => handleDeleteTerritory(t)}>Löschen</button>
                </div>
              ))}
            </div>
        </Modal>
      )}
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          onConfirm={confirm.onConfirm}
          secondaryLabel={confirm.secondaryLabel}
          onSecondary={confirm.onSecondary}
          onCancel={() => setConfirm(null)}
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}
      <Celebration />
      {splash && (
        <div className="login-splash" aria-hidden="true">
          <img src="/logo.png" alt="" />
          <div className="login-splash-tagline">System statt Zufall</div>
        </div>
      )}
    </div>
  );
}
